import i18n from '@/localisation/i18n';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useState } from 'react';
import {
  authorize,
  ResponseAuthorize,
  SubmitAuthorize,
} from '../api/authorize';
import useToastStore from '@/store/toast';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Appear } from '@/UI/animations/Appear';
import { setCookies } from '@/utils/cookie';

export const Confirm = ({
  isRegistered,
  email,
  otp,
  onBack,
}: {
  isRegistered: boolean;
  email: string;
  otp: string;
  onBack: () => void;
}) => {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [forceDisable, setForceDisable] = useState(false);

  const navigate = useNavigate();

  const addToast = useToastStore(state => state.addToast);

  const mutationAuthorize = useMutation({
    mutationFn: (data: SubmitAuthorize) => authorize(data),
    onSuccess: (data: ResponseAuthorize) => {
      if (data.isAuthorized) {
        setCookies('auth', data.token, 90);
        addToast(i18n.t('message.check.success'));

        navigate('/');
      } else {
        setCodeError(true);
        setForceDisable(false);
      }
    },
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      className="flex h-full w-full flex-col justify-center"
    >
      <div className="mb-6 flex items-center gap-2">
        <img
          src="/icons/back_arrow.svg"
          alt="arrow-left"
          className="cursor-pointer"
          onClick={onBack}
        />
        <h1 className="md:text-4md text-2xl font-bold">
          {isRegistered ? i18n.t('auth') : i18n.t('reg')}
        </h1>
      </div>
      <h3 className="mb-10 text-base md:text-lg">
        {isRegistered ? i18n.t('auth.text') : i18n.t('reg.text')}
      </h3>
      <div className="mb-9">
        <input
          value={code}
          type="text"
          className={clsx(
            'input input-bordered w-full gap-2 md:h-16 md:text-lg',
            codeError && 'input-error'
          )}
          placeholder={i18n.t('check.code')}
          onChange={e => {
            setCode(e.target.value);
            setForceDisable(false);
            setCodeError(false);
          }}
        />
        <Appear isVisible={codeError}>
          <div className="label">
            <span className="label-text-alt text-error">
              {i18n.t('wrong.code')}
            </span>
          </div>
        </Appear>
      </div>
      <button
        disabled={!code || forceDisable}
        className="custom-btn btn btn-primary w-full md:h-16 md:text-lg"
        onClick={() => {
          setForceDisable(true);

          mutationAuthorize.mutate({
            email,
            otp,
            verificationCode: code,
          });
        }}
      >
        {i18n.t('enter')}
      </button>
    </motion.div>
  );
};
